import React, { useState, useRef, useEffect } from "react";
import classNames from "classnames";

import { Image } from "../Image";
import { Svg, SVG_ICONS } from "../Svg/Svg";
import { Typography } from "../Typography/Typography";
import "./Carousel.scss";
import { useWindowSize } from "../../../hooks/window-hooks";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import { LayoutState } from "../../../store/reducers/layout-reducer";
import { ServiceEntity } from "../../../types/services";
import { useTranslation } from "react-i18next";

interface Props {
  services: ServiceEntity[];
  onClickRemoveService: (serviceCode: ServiceEntity) => void;
  testId?: string;
}

export const ServicesCarousel: React.FC<Props> = (props) => {
  const { windowSize } = useWindowSize();
  const [isDown, setisDown] = useState<boolean>(false);
  const [startX, setstartX] = useState<number>(0);
  const [scrollLeft, setscrollLeft] = useState<number>(0);
  const [isMaxLeft, setMaxLeft] = useState<boolean>(true);
  const [isMaxRight, setMaxRight] = useState<boolean>(false);
  const focusTargetRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (focusTargetRef.current) {
      focusTargetRef.current.focus();
      focusTargetRef.current = null;
    }
  }, [props.services]);

  const servicesRef = useRef<HTMLDivElement>(null);
  const layoutState = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );

  const onMouseDown = (e: any) => {
    if (!servicesRef.current) return;
    setisDown(true);
    setstartX(e.pageX - servicesRef.current.offsetLeft);
    setscrollLeft(servicesRef.current.scrollLeft);
  };

  const onMouseUp = (e: any) => {
    setisDown(false);
    e.preventDefault();
    e.stopPropagation();
  };

  const onMouseLeave = (e: any) => {
    e.preventDefault();
    setisDown(false);
  };

  const onMouseMove = (e: any) => {
    if (!servicesRef.current) return;
    e.preventDefault();
    const x: number = e.pageX - servicesRef.current.offsetLeft;
    const walk = x - startX;
    servicesRef.current.scrollLeft = scrollLeft - walk;
  };

  const onClickRight = (e: any) => {
    if (!servicesRef.current) return;
    let scrollAmount = 0;
    let slideTimer = setInterval(function () {
      if (servicesRef.current) {
        servicesRef.current.scrollLeft += 10;
      }
      scrollAmount += 10;
      if (scrollAmount >= 300) {
        window.clearInterval(slideTimer);
      }
    }, 10);
  };

  const onClickLeft = (e: any) => {
    if (!servicesRef.current) return;
    let scrollAmount = 0;
    let slideTimer = setInterval(function () {
      if (servicesRef.current) {
        servicesRef.current.scrollLeft -= 10;
      }
      scrollAmount += 10;
      if (scrollAmount >= 300) {
        window.clearInterval(slideTimer);
      }
    }, 10);
  };

  useEffect(
    () => {
      refreshArrowButtons();
    },
    // eslint-disable-next-line
    [props.services, windowSize, layoutState.isRTL]
  );

  const refreshArrowButtons = () => {
    const currentRef = servicesRef.current;
    const posLeft = currentRef?.scrollLeft ?? 0;
    const length = currentRef?.scrollWidth ?? 0;
    const right = currentRef?.clientWidth ?? 0;

    if (layoutState.isRTL) {
      setMaxRight(posLeft >= 0);
      setMaxLeft(length <= Math.abs(posLeft) + right + 10);
    } else {
      setMaxLeft(posLeft === 0);
      setMaxRight(length <= posLeft + right + 10);
    }
  };

  return (
    <div
      className={classNames("service-carousel-container", {
        "max-left": isMaxLeft,
        "max-right": isMaxRight,
      })}
      data-testid={props.testId}
    >
      <Svg
        icon={SVG_ICONS.CAROUSEL_ARROW}
        width={16}
        height={16}
        className="service-carousel-container__arrow-left"
        onClick={onClickLeft}
        testId="onclick-left"
      />
      <div
        data-testid="service-carousel"
        className="service-carousel"
        ref={servicesRef}
        onMouseDown={onMouseDown}
        onMouseLeave={onMouseLeave}
        onMouseUp={onMouseUp}
        onMouseMove={onMouseMove}
        onScroll={refreshArrowButtons}
      >
        {props.services.map((service) => {
          return (
            <ServiceCarouselItem
              key={service.service_code}
              icon={service.service_icon_url}
              name={service.service_name ? service.service_name : ""}
              onClickRemoveService={() => {
                props.onClickRemoveService(service);
              }}
              focusTargetRef={focusTargetRef}
            />
          );
        })}
      </div>
      <Svg
        testId="onclick-right"
        icon={SVG_ICONS.CAROUSEL_ARROW}
        width={16}
        height={16}
        className="service-carousel-container__arrow-right"
        onClick={onClickRight}
      />
    </div>
  );
};

interface ItemProps {
  icon: string;
  name: string;
  onClickRemoveService: () => void;
  focusTargetRef: React.MutableRefObject<HTMLElement | null>;
}

export const ServiceCarouselItem: React.FC<ItemProps> = (props) => {
  const { t } = useTranslation();
  const { isDarkMode } = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );
  const { name, icon, onClickRemoveService } = props;

  return (
    <div className="service-carousel__item">
      <div className="service-carousel__item__icon">
        <Image image={icon} width={24} height={24} />
      </div>
      <div className="service-carousel__item__name">
        <Typography
          className="service-carousel__item__name__text"
          variant="body3"
        >
          {name}
        </Typography>
      </div>
      <div
        className="service-carousel__item__close"
        onClick={(e) => {
          // Only allow mouse-triggered clicks to avoid multiple invocation for onClickRemoveService
          if (e.detail === 0) return;
          onClickRemoveService();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            const modalElement = document.getElementById("mydata-toast");
            if (!modalElement) return;

            const selectedService = Array.from(
              modalElement.querySelectorAll("button.selected-svc-close-btn")
            );

            const index = selectedService.indexOf(e.target as HTMLElement);
            if (index === -1) return;

            const nextElement =
              index + 1 < selectedService.length
                ? (selectedService[index + 1] as HTMLElement)
                : (selectedService[index - 1] as HTMLElement);

            // Save the element in a global ref (defined in ServicesCarousel)
            props.focusTargetRef.current = nextElement;

            // Trigger the removal callback
            onClickRemoveService();
          }
        }}
        data-testid="sc-rm-svc-btn"
      >
        <Svg
          icon={
            isDarkMode ? SVG_ICONS.DRAWER_CLOSE_DARK : SVG_ICONS.DRAWER_CLOSE
          }
          width={7.9}
          height={7.9}
          title={t("PRIVACY_COMMON_CLOSE_ALT_TEXT")}
        />
        <button className="selected-svc-close-btn"></button>
      </div>
    </div>
  );
};
